/**
 * The board rows. The drawing is the socket's job; this is the list around it.
 *
 * `GET /api/relay/boards?conversation=&meeting=` answers with the caller's own
 * boards plus those of the room named, which is why the Boards page passes the
 * query through untouched rather than filtering what comes back: the server
 * decides what a member may see.
 */

import { api } from '../api';
import type { Board } from './types';

interface BoardsAnswer {
  boards: Board[];
}

export interface BoardScope {
  conversationId?: string;
  meetingId?: string;
}

export async function listBoards(scope: BoardScope = {}): Promise<Board[]> {
  const query = new URLSearchParams();
  if (scope.conversationId) query.set('conversation', scope.conversationId);
  if (scope.meetingId) query.set('meeting', scope.meetingId);
  const suffix = query.toString();
  const { boards } = await api<BoardsAnswer>(`/api/relay/boards${suffix ? `?${suffix}` : ''}`);
  return boards;
}

export function getBoard(id: string): Promise<Board> {
  return api<Board>(`/api/relay/boards/${encodeURIComponent(id)}`);
}

export function createBoard(title: string, scope: BoardScope = {}): Promise<Board> {
  return api<Board>('/api/relay/boards', {
    method: 'POST',
    body: JSON.stringify({
      title,
      ...(scope.conversationId ? { conversationId: scope.conversationId } : {}),
      ...(scope.meetingId ? { meetingId: scope.meetingId } : {})
    })
  });
}

export function renameBoard(id: string, title: string): Promise<Board> {
  return api<Board>(`/api/relay/boards/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ title })
  });
}

/** Into a conversation the owner is in, or back to the owner alone (`null`).
 *  The owner only; a meeting's board stays with its meeting. */
export function shareBoard(id: string, conversationId: string | null): Promise<Board> {
  return api<Board>(`/api/relay/boards/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ conversationId })
  });
}

export function deleteBoard(id: string): Promise<{ ok: boolean }> {
  return api<{ ok: boolean }>(`/api/relay/boards/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
